import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ExternalLink, Plus, X } from 'lucide-react'
import {
  addPartnerEntityLink,
  createContractAmendment,
  loadContractAmendments,
  activateContractAmendment,
} from '@/services/contractAmendmentsService'
import { toRoyaltyBusinessDateKey } from '@/services/royaltyBusinessDate'
import { resolveCurrentContractEntityId } from '@/lib/contractParty'
import { AddPartnerEntityInline, resolveDefaultEntityCountry } from './AddPartnerEntityInline'
import type { ContractAmendment, ContractAmendmentType, PartnerLegalEntity } from '@/types'

// Предзаполнение формы ДС из родителя (когда правку поля договора перенаправляют в событие).
// seq — счётчик-триггер: каждый bump открывает форму заново, даже с теми же значениями.
export type AmendmentPrefill = {
  seq: number
  amendment_type: ContractAmendmentType
  effective_from?: string
  new_entity_id?: string
  new_expected_end_date?: string
}

const inputCls = 'w-full text-xs border border-border rounded px-2 py-1.5 bg-background disabled:opacity-60'

const TYPE_LABELS: Record<ContractAmendmentType, string> = {
  ENTITY_CHANGE: 'Смена юрлица',
  TERM_EXTENSION: 'Продление срока',
  TERMINATION: 'Расторжение',
  OTHER: 'Прочее / заметка',
}

const TYPE_HINTS: Record<ContractAmendmentType, string> = {
  ENTITY_CHANGE: 'Новая сторона ДКК с указанной даты. Номер и дата договора не меняются.',
  TERM_EXTENSION: 'Продление того же договора без нового номера — сдвигает расчётный конец срока.',
  TERMINATION: 'Расторжение: дата в ДС совпадает с датой фактического завершения договора.',
  OTHER: 'Событие-заметка в журнал. На расчётные интервалы не влияет.',
}

const DEBT_POLICY_LABELS: Record<'TRANSFERS' | 'STAYS', string> = {
  TRANSFERS: 'Долг переходит на новое юрлицо',
  STAYS: 'Долг остаётся за старым юрлицом',
}

const EFFECTIVE_FROM_LABELS: Record<ContractAmendmentType, string> = {
  ENTITY_CHANGE: 'Действует с',
  TERM_EXTENSION: 'Дата соглашения',
  TERMINATION: 'Дата расторжения',
  OTHER: 'Дата события',
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU')
}

function emptyForm() {
  return {
    amendment_type: 'ENTITY_CHANGE' as ContractAmendmentType,
    effective_from: '',
    new_entity_id: '',
    new_expected_end_date: '',
    debt_policy: 'TRANSFERS' as 'TRANSFERS' | 'STAYS',
    note: '',
    doc_url: '',
    activated: true,
  }
}

/**
 * Событийный слой ДКК: журнал ДС + текущая сторона договора. Форма создаёт
 * ENTITY_CHANGE / TERM_EXTENSION / TERMINATION / OTHER через create_contract_amendment
 * (advanced-gated). Правила payload по типу проверяет RPC; форма показывает только нужные поля.
 * Актуальная сторона выводится из базового юрлица договора + ENTITY_CHANGE-событий (view
 * contract_party_intervals закрыта для authenticated миграцией 20260707130000 — читать её из
 * фронта нельзя). См. миграцию 20260703100000 и docs/SERIES_CONTRACT_ARCHITECTURE_NEXT_STEPS.md.
 */
export function ContractAmendmentsEditor({
  contractId,
  entities,
  baseEntityId,
  partnerNumber,
  canEdit,
  protectedPeriodLabels = [],
  prefill,
  onEntityCreated,
  onActiveEntityChange,
  onChanged,
}: {
  contractId: number
  entities: PartnerLegalEntity[]
  baseEntityId: number | null
  partnerNumber?: number
  canEdit: boolean
  protectedPeriodLabels?: string[]
  prefill?: AmendmentPrefill | null
  onEntityCreated?: (entity: PartnerLegalEntity) => void
  onActiveEntityChange?: (entityId: number | null) => void
  onChanged?: () => void
}) {
  const [amendments, setAmendments] = useState<ContractAmendment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  // Отметка подписи у ДС имеет структурные последствия (расторжение вступает в
  // силу и закрывает условия роялти), поэтому она идёт через подтверждение.
  const [amendmentAction, setAmendmentAction] = useState<null | {
    amendmentId: number
    type: ContractAmendmentType
    effectiveFrom: string
  }>(null)
  const [amendmentDate, setAmendmentDate] = useState('')
  const [amendmentBusy, setAmendmentBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [newEntityIdsNeedingLink, setNewEntityIdsNeedingLink] = useState<Set<number>>(() => new Set())
  const rootRef = useRef<HTMLDivElement>(null)

  // Родитель перенаправил правку поля договора сюда: открыть форму с предзаполнением и подсветить.
  useEffect(() => {
    if (!prefill) return
    setForm({
      ...emptyForm(),
      amendment_type: prefill.amendment_type,
      effective_from: prefill.effective_from ?? '',
      new_entity_id: prefill.new_entity_id ?? '',
      new_expected_end_date: prefill.new_expected_end_date ?? '',
    })
    setShowForm(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.seq])

  const load = useCallback(() => {
    setLoading(true)
    loadContractAmendments(contractId)
      .then(a => {
        setAmendments(a)
        setError(null)
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [contractId])

  useEffect(() => {
    load()
  }, [load])

  const entityName = useMemo(() => {
    const map = new Map<number, string>()
    for (const e of entities) map.set(e.entity_id, e.legal_name)
    return (id: number | null) => (id == null ? '—' : map.get(id) ?? `Юрлицо #${id}`)
  }, [entities])

  function setField<K extends keyof ReturnType<typeof emptyForm>>(
    field: K,
    value: ReturnType<typeof emptyForm>[K]
  ) {
    setForm(f => ({ ...f, [field]: value }))
  }

  // Актуальная сторона на сегодня = базовое юрлицо договора, поверх которого применены прошедшие
  // ENTITY_CHANGE (amendments уже отсортированы по effective_from, amendment_id — побеждает последнее).
  const activeEntityId = useMemo(() => {
    const today = toRoyaltyBusinessDateKey(new Date()) ?? new Date().toISOString().slice(0, 10)
    return resolveCurrentContractEntityId(baseEntityId, amendments, today)
  }, [amendments, baseEntityId])

  useEffect(() => {
    onActiveEntityChange?.(activeEntityId)
  }, [activeEntityId, onActiveEntityChange])

  // Для смены юрлица предлагаем все юрлица партнёра, кроме текущей активной стороны.
  const entityOptions = useMemo(
    () => entities.filter(e => e.entity_id !== activeEntityId),
    [entities, activeEntityId]
  )

  const canSubmit = useMemo(() => {
    if (!form.effective_from) return false
    switch (form.amendment_type) {
      case 'ENTITY_CHANGE':
        return !!form.new_entity_id
      case 'TERM_EXTENSION':
        return !!form.new_expected_end_date
      case 'OTHER':
        return form.note.trim() !== ''
      default:
        return true
    }
  }, [form])

  async function handleSave() {
    if (!canSubmit) return
    setSaving(true)
    setError(null)
    try {
      const newEntityId =
        form.amendment_type === 'ENTITY_CHANGE' && form.new_entity_id
          ? parseInt(form.new_entity_id)
          : null
      if (form.amendment_type === 'ENTITY_CHANGE') {
        if (partnerNumber == null || newEntityId == null) {
          throw new Error('Не удалось определить франчайзи или новое юрлицо для ДС')
        }
        // Existing partner entities were linked by the event-layer backfill. Calling the
        // helper for every save is both redundant and wrong for a lawyer with standard
        // access: create_contract_amendment uses the lawyer gate, while this legacy helper
        // still uses the old advanced gate. Only a just-created inline entity needs a link.
        if (newEntityIdsNeedingLink.has(newEntityId)) {
          await addPartnerEntityLink(
            partnerNumber,
            newEntityId,
            `Автосвязь перед ДС ENTITY_CHANGE по договору ${contractId}`
          )
          setNewEntityIdsNeedingLink(current => {
            const next = new Set(current)
            next.delete(newEntityId)
            return next
          })
        }
      }
      await createContractAmendment({
        contract_id: contractId,
        amendment_type: form.amendment_type,
        effective_from: form.effective_from,
        new_entity_id: newEntityId,
        new_expected_end_date:
          form.amendment_type === 'TERM_EXTENSION' ? form.new_expected_end_date || null : null,
        debt_policy: form.amendment_type === 'ENTITY_CHANGE' ? form.debt_policy : null,
        note: form.note.trim() || null,
        doc_url: form.doc_url.trim() || null,
        activated: form.activated,
      })
      setShowForm(false)
      setForm(emptyForm())
      load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const draftCount = amendments.filter(a => a.activated_at == null).length

  return (
    <div ref={rootRef} className="mt-4 border-t border-border pt-3 scroll-mt-4">
      <div className="mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold">Дополнительные соглашения (ДС)</div>
          {draftCount > 0 && (
            <span className="rounded bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning">
              {draftCount} ждут подписи франчайзи
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          События договора: смена юрлица, продление срока, расторжение. Ошибочные ДС можно
          исправлять через advanced-действия.
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Текущая сторона: <span className="text-foreground font-medium">{entityName(activeEntityId)}</span>
        </p>
      </div>

      {protectedPeriodLabels.length > 0 && (
        <div className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
          Договор участвует в зафиксированных периодах: {protectedPeriodLabels.join(', ')}.
          Структурное ДС с датой внутри них будет отклонено базой. Продление документального срока
          не меняет расчёт роялти и оформляется без снятия фиксации.
        </div>
      )}

      {error && (
        <div className="text-xs text-destructive flex items-center gap-1.5 mb-2">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground py-3">Загрузка событий...</div>
      ) : (
        <div className="space-y-2">
          {amendments.length === 0 && !showForm && (
            <div className="text-center text-muted-foreground py-4 text-sm">
              Дополнительных соглашений нет
            </div>
          )}

          {amendments.map(a => (
            <div key={a.amendment_id} className="rounded-md border border-border p-3">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-sm font-semibold">{TYPE_LABELS[a.amendment_type]}</div>
                <div className="text-xs text-muted-foreground">
                  {a.amendment_type === 'TERM_EXTENSION'
                    ? `ДС от ${formatDate(a.effective_from)}`
                    : `c ${formatDate(a.effective_from)}`}
                </div>
              </div>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className={a.activated_at ? 'text-emerald-700' : 'text-warning'}>
                  {a.activated_at
                    ? `Франчайзи подписал ${formatDate(a.activated_at.slice(0, 10))}`
                    : 'Заготовка: франчайзи не подписал, ДС не действует'}
                </span>
                {canEdit && !a.activated_at && (
                  <button
                    type="button"
                    onClick={() => {
                      setAmendmentAction({
                        amendmentId: a.amendment_id,
                        type: a.amendment_type,
                        effectiveFrom: a.effective_from,
                      })
                      setAmendmentDate(
                        a.signed_at
                        ?? toRoyaltyBusinessDateKey(new Date())
                        ?? new Date().toISOString().slice(0, 10)
                      )
                    }}
                    className="rounded border border-border px-2 py-0.5 text-[11px] hover:bg-muted"
                  >
                    Франчайзи подписал
                  </button>
                )}
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {a.amendment_type === 'ENTITY_CHANGE' && (
                  <div>
                    Новая сторона: <span className="text-foreground font-medium">{entityName(a.new_entity_id)}</span>
                    {a.debt_policy && ` · ${DEBT_POLICY_LABELS[a.debt_policy]}`}
                  </div>
                )}
                {a.amendment_type === 'TERM_EXTENSION' && (
                  <div>
                    Новый срок до:{' '}
                    <span className="text-foreground font-medium">{formatDate(a.new_expected_end_date)}</span>
                  </div>
                )}
                {a.amendment_type === 'TERMINATION' && (
                  <div>Фактическое завершение: {formatDate(a.effective_from)}</div>
                )}
                {a.note && <div className="italic">{a.note}</div>}
                {a.doc_url && (
                  <a
                    href={a.doc_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <ExternalLink size={11} /> Документ
                  </a>
                )}
              </div>
            </div>
          ))}

          {canEdit && showForm && (
            <div
              className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
              onClick={() => {
                setShowForm(false)
                setForm(emptyForm())
              }}
            >
              <div
                className="w-full max-w-lg rounded-lg bg-background p-4 shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">Новое дополнительное соглашение</p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false)
                      setForm(emptyForm())
                    }}
                    className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="Закрыть"
                  >
                    <X size={15} />
                  </button>
                </div>
                {error && (
                  <div
                    className="mb-3 flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs text-destructive"
                    role="alert"
                  >
                    <AlertTriangle size={12} className="mt-0.5 flex-none" />
                    <span>{error}</span>
                  </div>
                )}
                {protectedPeriodLabels.length > 0 && form.amendment_type !== 'TERM_EXTENSION' && (
                  <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
                    Договор участвует в зафиксированных периодах: {protectedPeriodLabels.join(', ')}.
                    ДС, влияющее на эти месяцы, будет отклонено базой. Сначала отмените роялти и
                    снимите фиксацию выручки; после изменения зафиксируйте и выставьте роялти заново.
                  </div>
                )}
                {protectedPeriodLabels.length > 0 && form.amendment_type === 'TERM_EXTENSION' && (
                  <div className="mb-3 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-2 text-xs">
                    Продление меняет только документальный срок договора и не пересчитывает
                    зафиксированные периоды {protectedPeriodLabels.join(', ')}. Снимать фиксацию не нужно.
                  </div>
                )}
                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] text-muted-foreground">Тип события</label>
                    <select
                      value={form.amendment_type}
                      onChange={e => setField('amendment_type', e.target.value as ContractAmendmentType)}
                      className={inputCls}
                    >
                      {(Object.keys(TYPE_LABELS) as ContractAmendmentType[]).map(t => (
                        <option key={t} value={t}>
                          {TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {TYPE_HINTS[form.amendment_type]}
                    </p>
                  </div>

                  <div>
                    <label className="text-[10px] text-muted-foreground">{EFFECTIVE_FROM_LABELS[form.amendment_type]}</label>
                    <input
                      type="date"
                      value={form.effective_from}
                      onChange={e => setField('effective_from', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  {form.amendment_type === 'TERMINATION' && form.effective_from && (
                    <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                      <div className="flex items-start gap-1.5">
                        <AlertTriangle size={12} className="mt-0.5 flex-none" />
                        <span>
                          При сохранении ДС о расторжении условия роялти по этому договору будут закрыты с {formatDate(form.effective_from)}.
                          Если есть зафиксированные периоды роялти, база отклонит изменение и попросит сначала снять фиксацию.
                        </span>
                      </div>
                    </div>
                  )}

                  {form.amendment_type === 'ENTITY_CHANGE' && (
                    <>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Новое юрлицо (сторона)</label>
                        <select
                          value={form.new_entity_id}
                          onChange={e => setField('new_entity_id', e.target.value)}
                          className={inputCls}
                        >
                          <option value="">— выберите юрлицо —</option>
                          {entityOptions.map(e => (
                            <option key={e.entity_id} value={String(e.entity_id)}>
                              {e.legal_name} · ИНН {e.inn ?? '—'}
                            </option>
                          ))}
                        </select>
                        {entityOptions.length === 0 && (
                          <p className="text-[10px] text-warning mt-0.5">
                            У франчайзи нет других юрлиц — заведите новое ниже.
                          </p>
                        )}
                        {partnerNumber != null && (
                          <AddPartnerEntityInline
                            partnerNumber={partnerNumber}
                            defaultCountry={resolveDefaultEntityCountry(entities)}
                            onCreated={e => {
                              onEntityCreated?.(e)
                              setNewEntityIdsNeedingLink(current => new Set(current).add(e.entity_id))
                              setField('new_entity_id', String(e.entity_id))
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground">Политика долга</label>
                        <select
                          value={form.debt_policy}
                          onChange={e => setField('debt_policy', e.target.value as 'TRANSFERS' | 'STAYS')}
                          className={inputCls}
                        >
                          {(Object.keys(DEBT_POLICY_LABELS) as Array<'TRANSFERS' | 'STAYS'>).map(p => (
                            <option key={p} value={p}>
                              {DEBT_POLICY_LABELS[p]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {form.amendment_type === 'TERM_EXTENSION' && (
                    <div>
                      <label className="text-[10px] text-muted-foreground">Новый срок до (включительно)</label>
                      <input
                        type="date"
                        value={form.new_expected_end_date}
                        onChange={e => setField('new_expected_end_date', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] text-muted-foreground">
                      Комментарий{form.amendment_type === 'OTHER' ? ' (обязателен)' : ''}
                    </label>
                    <input
                      type="text"
                      value={form.note}
                      onChange={e => setField('note', e.target.value)}
                      placeholder="Основание / примечание"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">Ссылка на скан ДС</label>
                    <input
                      type="url"
                      value={form.doc_url}
                      onChange={e => setField('doc_url', e.target.value)}
                      placeholder="https://…"
                      className={inputCls}
                    />
                  </div>

                  <label className={`flex items-start gap-2 text-xs ${form.activated ? 'text-emerald-700' : 'text-warning'}`}>
                    <input
                      type="checkbox"
                      checked={form.activated}
                      onChange={e => setField('activated', e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      Франчайзи подписал документ
                      {!form.activated && (
                        <span className="block text-[10px] text-muted-foreground">
                          Без отметки ДС заводится заготовкой: оно не меняет срок, сторону
                          договора и условия роялти, пока подпись не будет отмечена.
                        </span>
                      )}
                    </span>
                  </label>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false)
                        setForm(emptyForm())
                      }}
                      className="text-xs text-muted-foreground"
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || !canSubmit}
                      className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-lg disabled:opacity-50"
                    >
                      {saving ? 'Сохранение...' : 'Добавить'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {canEdit && !showForm && (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground border border-dashed border-border rounded-lg py-2.5 hover:text-foreground hover:border-foreground transition-colors"
              >
                <Plus size={13} /> Добавить дополнительное соглашение
              </button>
          )}

          {!canEdit && (
            <div className="text-[10px] text-muted-foreground">
              Добавление ДС доступно только пользователям УК с расширенным доступом.
            </div>
          )}
        </div>
      )}

      {amendmentAction && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-4 shadow-lg">
            <div className="text-sm font-semibold">
              Франчайзи подписал ДС
            </div>

            <>
                <p className="mt-2 text-xs text-muted-foreground">
                  ДС вступит в силу и начнёт учитываться расчётом роялти.
                  {amendmentAction.type === 'TERMINATION' &&
                    ` Условия роялти по договору будут закрыты с ${formatDate(amendmentAction.effectiveFrom)}.`}
                  {amendmentAction.type === 'ENTITY_CHANGE' &&
                    ' Стороной договора станет новое юрлицо с указанной даты.'}
                  {amendmentAction.type === 'TERM_EXTENSION' &&
                    ' Расчётный срок договора сдвинется на новую дату.'}
                  {amendmentAction.type !== 'TERM_EXTENSION' &&
                    ' Если дата попадает в уже выставленный период, база отклонит операцию.'}
                  {amendmentAction.type === 'TERM_EXTENSION' &&
                    ' Зафиксированные периоды роялти при этом не пересчитываются.'}
                </p>
                <div className="mt-3 rounded-md bg-muted/50 px-2.5 py-2 text-xs">
                  <div className="text-[10px] text-muted-foreground">
                    {EFFECTIVE_FROM_LABELS[amendmentAction.type]}
                  </div>
                  <div className="font-medium">{formatDate(amendmentAction.effectiveFrom)}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    Эта дата задана в черновике и при подписании не меняется.
                  </div>
                </div>
                <label className="mt-3 block text-[10px] text-muted-foreground">Дата подписания документа</label>
                <input
                  type="date"
                  value={amendmentDate}
                  onChange={e => setAmendmentDate(e.target.value)}
                  required
                  className={inputCls}
                />
            </>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAmendmentAction(null)}
                disabled={amendmentBusy}
                className="rounded-md border border-border px-3 py-1.5 text-xs"
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={
                  amendmentBusy ||
                  !amendmentDate
                }
                onClick={async () => {
                  setAmendmentBusy(true)
                  try {
                    await activateContractAmendment(amendmentAction.amendmentId, amendmentDate || null)
                    setAmendmentAction(null)
                    load()
                    onChanged?.()
                  } catch (err) {
                    setError(err instanceof Error ? err.message : String(err))
                    setAmendmentAction(null)
                  } finally {
                    setAmendmentBusy(false)
                  }
                }}
                className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
              >
                {amendmentBusy
                  ? 'Сохраняем…'
                  : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
